import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COUNTRIES = [
  "United States", "United Kingdom", "United Arab Emirates", "Singapore", "Switzerland",
  "Canada", "Australia", "Germany", "France", "Hong Kong", "Japan", "Other",
];

export default function Intake() {
  const navigate = useNavigate();
  const { member, setMember } = useAuth();
  const [form, setForm] = useState({
    full_name: member?.name || "",
    email: member?.email || "",
    phone: "",
    country: "",
    role: "",
    financial_category: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.country || !form.role) {
      toast.error("Please complete all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/intake", form);
      setMember(res.data);
      toast.success("Private intake submitted.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <p className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-emerald-400/80 mb-3">
        Step 01 ◆ Private Intake
      </p>
      <h1 className="font-serif-display text-3xl sm:text-4xl tracking-tight text-slate-50 leading-tight">
        Set up your member profile
      </h1>
      <p className="text-sm text-slate-400 mt-3 leading-relaxed">
        Basic profile, residency and your intended role. This is completed inside the Club — never in public forms.
      </p>

      <form onSubmit={submit} className="mt-8 space-y-5">
        <Field label="Full legal name">
          <input
            data-testid="intake-fullname-input"
            className="hairline-input w-full rounded-lg px-4 py-3 text-sm"
            value={form.full_name}
            onChange={(e) => set("full_name", e.target.value)}
            placeholder="As shown on government ID"
          />
        </Field>
        <Field label="Email">
          <input
            data-testid="intake-email-input"
            type="email"
            className="hairline-input w-full rounded-lg px-4 py-3 text-sm"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
          />
        </Field>
        <Field label="Phone">
          <input
            data-testid="intake-phone-input"
            className="hairline-input w-full rounded-lg px-4 py-3 text-sm"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+1 555 000 0000"
          />
        </Field>
        <Field label="Country of primary residence">
          <Select value={form.country} onValueChange={(v) => set("country", v)}>
            <SelectTrigger
              data-testid="intake-country-select"
              className="hairline-input w-full rounded-lg px-4 py-3 text-sm h-auto"
            >
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent className="bg-[#0D131C] border-slate-800 text-slate-200">
              {COUNTRIES.map((c) => (
                <SelectItem key={c} value={c} data-testid={`country-option-${c}`}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Intended role">
          <div className="grid grid-cols-2 gap-3">
            {[
              { v: "investor", t: "Investor", d: "Qualify & onboard" },
              { v: "introducer", t: "Introducer", d: "Refer the network" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.v}
                data-testid={`role-option-${opt.v}`}
                onClick={() => set("role", opt.v)}
                className={`text-left rounded-lg border p-4 transition-colors ${
                  form.role === opt.v
                    ? "border-emerald-500/60 bg-emerald-950/30"
                    : "border-slate-800 bg-[#0D131C]/60 hover:border-slate-700"
                }`}
              >
                <p className="text-sm font-medium text-slate-100">{opt.t}</p>
                <p className="font-mono-ui text-[9px] uppercase tracking-[0.15em] text-slate-500 mt-1">{opt.d}</p>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Financial category (optional)">
          <Select value={form.financial_category} onValueChange={(v) => set("financial_category", v)}>
            <SelectTrigger
              data-testid="intake-financial-select"
              className="hairline-input w-full rounded-lg px-4 py-3 text-sm h-auto"
            >
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="bg-[#0D131C] border-slate-800 text-slate-200">
              <SelectItem value="hnwi">High Net Worth Individual</SelectItem>
              <SelectItem value="family_office">Family Office</SelectItem>
              <SelectItem value="institutional">Institutional</SelectItem>
              <SelectItem value="professional">Professional Investor</SelectItem>
            </SelectContent>
          </Select>
        </Field>

        <button
          type="submit"
          disabled={submitting}
          data-testid="intake-form-submit-button"
          className="w-full flex items-center justify-center gap-2 bg-emerald-500 text-slate-950 font-medium rounded-xl py-4 hover:bg-emerald-400 transition-colors active:scale-[0.99] disabled:opacity-50"
        >
          {submitting ? "Submitting…" : "Submit Private Intake"}
          {!submitting && <ChevronRight className="w-4 h-4" />}
        </button>
      </form>
    </Layout>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block font-mono-ui text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">{label}</label>
    {children}
  </div>
);
